import React from 'react';
import {
  aggregateFromGradeCode,
  computePleAggregateTotal,
  resolvePleDivision,
  resolveSubjectAggregate
} from './aggregateUtils';

const gradeColor = (grade) => {
  if (!grade) return '#2e7d32';
  const g = grade.toUpperCase();
  if (!/^[ABCD]/.test(g)) return '#1565c0';
  if (g.startsWith('A')) return '#1b5e20';
  if (g.startsWith('B')) return '#2e7d32';
  if (g.startsWith('C')) return '#e65100';
  return '#b71c1c';
};

const assessmentCellValue = (assessment) => {
  if (!assessment) return '-';
  if (assessment.is_absent) return 'ABS';
  if (assessment.is_exempted) return 'EXM';
  if (assessment.rubric_rating_label) return assessment.rubric_rating_label;
  const weighted = assessment.weighted_score;
  if (weighted != null) return Number(weighted).toFixed(0);
  const raw = assessment.score ?? assessment.marks_obtained ?? assessment.raw_score;
  if (raw == null) return '-';
  return Number(raw).toFixed(0);
};

export default function SubjectsTable({ report, showSubjectComments = true, showSubjectRank = false, assessmentDisplayMode = 'weighted', showAggregate = true, showTotalAggregate = false, showMarks = true, showGrade = true }) {
  const subjects = React.useMemo(() => report?.subjects_data || [], [report?.subjects_data]);
  const aggregateTotal = React.useMemo(
    () => report?.aggregate_total ?? computePleAggregateTotal(subjects, report?.subjects_counted ?? 4),
    [report?.aggregate_total, report?.subjects_counted, subjects]
  );
  const division = React.useMemo(
    () => report?.division || resolvePleDivision(aggregateTotal),
    [report?.division, aggregateTotal]
  );
  const totalMarksAggregate = React.useMemo(() => {
    const fromOverallGrade = aggregateFromGradeCode(report?.overall_grade);
    if (fromOverallGrade != null) return fromOverallGrade;

    const numericOverallAggregate = Number(report?.overall_aggregate);
    if (Number.isFinite(numericOverallAggregate) && numericOverallAggregate > 0) {
      return Math.trunc(numericOverallAggregate);
    }

    return null;
  }, [report?.overall_grade, report?.overall_aggregate]);

  // Collect only include_in_final assessment types across all subjects/papers
  const assessmentCols = React.useMemo(() => {
    const map = new Map();
    const projectKeywords = /^(proj|project|assign|assignment|prac|practical|coursework|cw)/i;
    
    subjects.forEach((sub) => {
      (sub.papers || []).forEach((paper) => {
        (paper.assessments || []).forEach((a) => {
          if (a.include_in_final === false || a.policy_included === false) return;
          const code = a.assessment_type?.assessment_code || a.assessment_code;
          // Skip project assessments - they go in ProjectsTable
          if (code && !projectKeywords.test(code) && !map.has(code)) {
            map.set(code, { code, max_marks: a.max_marks, weight: a.weight_percentage });
          }
        });
      });
    });
    const caKeywords = /^(bot|mot|ca|mid|cont|test|quiz|hw|home)/i;
    const eotKeywords = /^(eot|exam|end|final|term)/i;
    const order = (code) => {
      if (caKeywords.test(code)) return 0;
      if (eotKeywords.test(code)) return 2;
      return 1;
    };
    return Array.from(map.values()).sort((a, b) => order(a.code) - order(b.code));
  }, [subjects]);

  const singleCol = assessmentCols.length === 1 || assessmentDisplayMode === 'raw';

  // Per-assessment aggregate total and division (raw mode only)
  const colAggregates = React.useMemo(() => {
    if (assessmentDisplayMode !== 'raw') return {};
    const result = {};
    assessmentCols.forEach((col) => {
      const grades = subjects.map((sub) => {
        let found = null;
        (sub.papers || []).forEach((paper) => {
          (paper.assessments || []).forEach((a) => {
            const code = a.assessment_type?.assessment_code || a.assessment_code;
            if (code === col.code) found = a;
          });
        });
        return found?.assessment_grade ?? null;
      }).filter(Boolean);
      const aggs = grades.map(aggregateFromGradeCode).filter((v) => Number.isInteger(v) && v > 0).sort((a, b) => a - b);
      const bestCount = report?.subjects_counted ?? 4;
      const colAggTotal = aggs.slice(0, bestCount).reduce((s, v) => s + v, 0) || null;
      result[col.code] = { aggTotal: colAggTotal, division: resolvePleDivision(colAggTotal) };
    });
    return result;
  }, [assessmentCols, assessmentDisplayMode, subjects, report?.subjects_counted]);

  const rawGradeCols = assessmentDisplayMode === 'raw' ? assessmentCols.length : 0;
  const showAggCol = showAggregate && showGrade && assessmentDisplayMode !== 'raw';
  const fixedCols = 1 + (showSubjectRank ? 1 : 0) + (showMarks ? assessmentCols.length + rawGradeCols + (singleCol ? 0 : 1) : 0) + (showAggCol ? 1 : 0) + (assessmentDisplayMode !== 'raw' && showSubjectComments ? 1 : 0);

  return (
    <>
      {!singleCol && <p className="rc-section-title">Academic Progress</p>}

      <table className="rc-table">
        <thead>
          <tr>
            <th className="left" style={{ width: '15%' }}>Subject</th>
            {showMarks && assessmentCols.map((col) => (
              <React.Fragment key={col.code}>
                <th style={{ textAlign: 'center', width: '5%' }}>
                  {col.code}<br />
                  <span style={{ fontWeight: 'normal', fontSize: '7pt' }}>
                    {assessmentDisplayMode === 'raw' ? `(/${col.max_marks})` : `(${col.weight}%)`}
                  </span>
                </th>
                {assessmentDisplayMode === 'raw' && (
                  <th style={{ textAlign: 'center', width: '3%', fontSize: '7pt' }}>Agg</th>
                )}
              </React.Fragment>
            ))}
            {showMarks && !singleCol && <th style={{ width: '5%', textAlign: 'center' }}>Total<br /><span style={{ fontWeight: 'normal', fontSize: '7pt' }}>(/100)</span></th>}
            {showSubjectRank && <th style={{ width: '4%', textAlign: 'center' }}>Rank</th>}
            {showAggCol && <th style={{ width: '3%' }}>Agg</th>}
            {assessmentDisplayMode !== 'raw' && showSubjectComments && <th className="left" style={{ width: '35%' }}>Remarks</th>}
            {assessmentDisplayMode !== 'raw' && <th style={{ width: '4%', textAlign: 'center' }}>Initials</th>}
          </tr>
        </thead>
        <tbody>
          {subjects.length === 0 ? (
            <tr><td colSpan={fixedCols} style={{ textAlign: 'center', color: '#888', padding: '10px' }}>No subject data available</td></tr>
          ) : subjects.map((sub, si) => {
            const papers = sub.papers || [];
            const grade = sub.rubric_rating_label || sub.subject_grade || sub.final_grade || '-';
            const subjectAggregate = resolveSubjectAggregate(sub);
            const subjectAggregateLabel = grade && grade !== '-'
              ? grade
              : (subjectAggregate != null ? String(subjectAggregate) : '-');
            const subjectName = sub.subject?.subject_name || sub.subject?.name || `Subject ${si + 1}`;
            const subjectRemark = sub.subject_comment || sub.teacher_comment || sub.subject_remarks || '-';

            // Flatten all assessments for this subject keyed by assessment_code
            const markByCode = {};
            papers.forEach((paper) => {
              (paper.assessments || []).forEach((a) => {
                const code = a.assessment_type?.assessment_code || a.assessment_code;
                if (code) markByCode[code] = a;
              });
            });

            const totalPct = assessmentCols.reduce((sum, col) => {
              const a = markByCode[col.code];
              if (!a || a.is_absent || a.is_exempted) return sum;
              const ws = parseFloat(a.weighted_score);
              return sum + (isNaN(ws) ? 0 : ws);
            }, 0);

            return (
              <tr key={sub.subject?.id || si}>
                <td className="subject-name">{subjectName}</td>
                {showMarks && assessmentCols.map((col) => {
                  const a = markByCode[col.code];
                  const val = assessmentDisplayMode === 'raw'
                    ? (() => {
                        if (!a) return '-';
                        if (a.is_absent) return 'ABS';
                        if (a.is_exempted) return 'EXM';
                        if (a.rubric_rating_label) return a.rubric_rating_label;
                        const raw = a.score ?? a.marks_obtained ?? a.raw_score;
                        return raw != null ? Number(raw).toFixed(0) : '-';
                      })()
                    : assessmentCellValue(a);
                  const isExcluded = a?.policy_included === false;
                  return (
                    <React.Fragment key={col.code}>
                      <td
                        style={{
                          textAlign: 'center',
                          fontSize: '8.5pt',
                          color: isExcluded ? '#9e9e9e' : 'inherit',
                          fontStyle: isExcluded ? 'italic' : 'normal'
                        }}
                      >
                        {val}{isExcluded ? '*' : ''}
                      </td>
                      {assessmentDisplayMode === 'raw' && (
                        <td style={{ textAlign: 'center', fontSize: '8pt', color: gradeColor(a?.assessment_grade) }}>
                          {a?.assessment_grade || '-'}
                        </td>
                      )}
                    </React.Fragment>
                  );
                })}
                {showMarks && !singleCol && (
                  <td style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '8.5pt' }}>
                    {totalPct.toFixed(0)}
                  </td>
                )}
                            {showSubjectRank && (
                  <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#555' }}>
                    {sub.subject_rank != null && sub.subject_total_students != null
                      ? `${sub.subject_rank}/${sub.subject_total_students}`
                      : '-'}
                  </td>
                )}
                {showAggCol && (
                  <td style={{ textAlign: 'center' }}>
                    <span className="rc-grade-badge" style={{ color: gradeColor(grade) }}>
                      {subjectAggregateLabel}
                    </span>
                  </td>
                )}
                {assessmentDisplayMode !== 'raw' && showSubjectComments && (
                  <td style={{ fontSize: '8.5pt', fontStyle: 'italic', color: '#444' }}>{subjectRemark}</td>
                )}
                {assessmentDisplayMode !== 'raw' && (
                  <td style={{ textAlign: 'center', fontSize: '8.5pt', fontWeight: 'bold' }}>
                    {(() => {
                      let initials = null;
                      (sub.papers || []).forEach((p) => { if (!initials && p.teacher_initials) initials = p.teacher_initials; });
                      return initials || '';
                    })()}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 'bold', background: '#f5f5f5' }}>
            <td className="subject-name">Totals</td>
            {showMarks && assessmentCols.map((col) => {
              if (assessmentDisplayMode !== 'raw') return <td key={col.code} />;
              let colTotal = 0;
              subjects.forEach((sub) => {
                const markByCode = {};
                (sub.papers || []).forEach((paper) => {
                  (paper.assessments || []).forEach((a) => {
                    const code = a.assessment_type?.assessment_code || a.assessment_code;
                    if (code) markByCode[code] = a;
                  });
                });
                const a = markByCode[col.code];
                if (!a || a.is_absent || a.is_exempted) return;
                const raw = parseFloat(a.score ?? a.marks_obtained ?? a.raw_score);
                if (!isNaN(raw)) colTotal += raw;
              });
              return (
                <React.Fragment key={col.code}>
                  <td style={{ textAlign: 'center' }}>{colTotal.toFixed(0)}</td>
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {showAggregate ? (colAggregates[col.code]?.aggTotal ?? '-') : ''}
                  </td>
                </React.Fragment>
              );
            })}
            {!singleCol && <td style={{ textAlign: 'center' }}>{subjects.reduce((sum, sub) => {
              const markByCode = {};
              (sub.papers || []).forEach((paper) => {
                (paper.assessments || []).forEach((a) => {
                  const code = a.assessment_type?.assessment_code || a.assessment_code;
                  if (code) markByCode[code] = a;
                });
              });
              const t = assessmentCols.reduce((s, col) => {
                const a = markByCode[col.code];
                if (!a || a.is_absent || a.is_exempted) return s;
                const ws = parseFloat(a.weighted_score);
                return s + (isNaN(ws) ? 0 : ws);
              }, 0);
              return sum + t;
            }, 0).toFixed(0)}</td>}
            {showSubjectRank && <td />}
            {showAggCol && <td />}
            {assessmentDisplayMode !== 'raw' && showSubjectComments && <td />}
            {assessmentDisplayMode !== 'raw' && <td />}
          </tr>
          {showAggCol && assessmentDisplayMode !== 'raw' && false && (
            <tr style={{ fontWeight: 'bold', background: '#f7f7f7' }}>
              <td className="subject-name">Aggregate Total</td>
              {assessmentCols.map((col) => (
                <React.Fragment key={col.code}>
                  <td />
                  {assessmentDisplayMode === 'raw' && <td />}
                </React.Fragment>
              ))}
              {!singleCol && <td />}
              {showSubjectRank && <td />}
              {showAggCol && <td style={{ textAlign: 'center' }}>{aggregateTotal ?? '-'}</td>}
              {assessmentDisplayMode !== 'raw' && showSubjectComments && <td />}
            </tr>
          )}
          {(showAggregate && assessmentDisplayMode === 'raw') && (
            <tr style={{ fontWeight: 'bold', background: '#f0f0f0' }}>
              <td className="subject-name">Division</td>
              {assessmentCols.map((col) => (
                <React.Fragment key={col.code}>
                  <td />
                  {assessmentDisplayMode === 'raw' && (
                    <td style={{ textAlign: 'center' }}>{colAggregates[col.code]?.division || '-'}</td>
                  )}
                </React.Fragment>
              ))}
              {!singleCol && <td />}
              {showSubjectRank && <td />}
              {showAggCol && <td style={{ textAlign: 'center' }}>{division || '-'}</td>}
              {assessmentDisplayMode !== 'raw' && showSubjectComments && <td />}
            </tr>
          )}
          {assessmentDisplayMode === 'raw' && (
          <tr style={{ fontWeight: 'bold', background: '#eeeeee' }}>
            <td className="subject-name">Position</td>
            {assessmentCols.map((col) => {
              let pos = null, posTotalStudents = null;
              for (const sub of subjects) {
                const markByCode = {};
                (sub.papers || []).forEach((paper) => {
                  (paper.assessments || []).forEach((a) => {
                    const code = a.assessment_type?.assessment_code || a.assessment_code;
                    if (code) markByCode[code] = a;
                  });
                });
                const a = markByCode[col.code];
                if (a?.assessment_rank != null) { pos = a.assessment_rank; posTotalStudents = a.total_students ?? a.assessment_total_students ?? null; break; }
              }
              const posDisplay = pos != null
                ? (posTotalStudents != null ? `${pos}/${posTotalStudents}` : `${pos}`)
                : '-';
              return (
                <React.Fragment key={col.code}>
                  <td style={{ textAlign: 'center' }}>{posDisplay}</td>
                  <td />
                </React.Fragment>
              );
            })}
            {!singleCol && <td />}
            {showSubjectRank && <td />}
            {showAggCol && <td />}
          </tr>
          )}
        </tfoot>
      </table>

    </>
  );
}
